import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

/**
 * Emits <meta name="robots" content="noindex, nofollow"> for any route that
 * must never end up in a search index:
 *
 *  - the admin CMS (/admin/**) and its login screen
 *  - any standalone /login, /reset-password, /verify screen
 *  - post-submission /thank-you pages (thin, duplicate content)
 *  - ANY url carrying a session/token-style query parameter, which can leak
 *    a one-time credential into search results if indexed
 *
 * Rendered once inside the router in App.js, so it applies everywhere without
 * each page having to remember to pass `noIndex` to <Seo />.
 */
const PRIVATE_PREFIXES = [
  "/admin",
  "/login",
  "/signin",
  "/sign-in",
  "/reset-password",
  "/forgot-password",
  "/verify",
  "/thank-you",
  "/thankyou",
];

const TOKEN_PARAMS = [
  "token",
  "access_token",
  "refresh_token",
  "auth",
  "session",
  "session_id",
  "code",
  "otp",
  "invite",
  "reset",
  "key",
  "apikey",
  "signature",
];

export function isNoIndexLocation(pathname = "/", search = "") {
  const path = pathname.toLowerCase().replace(/\/+$/, "") || "/";

  if (PRIVATE_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) {
    return true;
  }

  // Hash-fragment tokens never reach the server, but query params do.
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  for (const name of TOKEN_PARAMS) {
    if (params.has(name)) return true;
  }

  return false;
}

export default function NoIndexGuard() {
  const { pathname, search } = useLocation();
  const blocked = isNoIndexLocation(pathname, search);

  // A page-level <Seo> renders after this guard and would otherwise leave an
  // "index, follow" robots tag — and a self-referencing canonical — next to
  // ours. A canonical on a noindex page is a contradictory signal (it invites
  // the crawler to treat the URL as the indexable original), so strip it too.
  useEffect(() => {
    if (!blocked) return;
    const enforce = () => {
      document
        .querySelectorAll('meta[name="robots"], meta[name="googlebot"], meta[name="bingbot"]')
        .forEach((el) => {
          if (!/noindex/i.test(el.getAttribute("content") || "")) {
            el.setAttribute("content", "noindex, nofollow");
          }
        });
      document
        .querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]')
        .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
      document
        .querySelectorAll('meta[property="og:url"]')
        .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    };
    enforce();
    const observer = new MutationObserver(enforce);
    observer.observe(document.head, { childList: true, subtree: true, attributes: true });
    return () => observer.disconnect();
  }, [blocked, pathname, search]);


  if (!blocked) return null;


  return (
    <Helmet prioritizeSeoTags>
      <meta name="robots" content="noindex, nofollow" />
      <meta name="googlebot" content="noindex, nofollow" />
      <meta name="bingbot" content="noindex, nofollow" />
    </Helmet>
  );
}
